import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { GroceryItem } from "../../types/grocery";
import { useSharedListMetadata } from "../../hooks/useSharedListMetadata";
import { useSharedListItems } from "../../hooks/useSharedListItems";
import { useExpirationTimer } from "../../hooks/useExpirationTimer";
import { createDebug } from "../../utils/debug";
import { OfflineStatus } from "../OfflineStatus/OfflineStatus";
import IOSInputBar from "./IOSInputBar";
import "./SharedGroceryList.scss";
import { formatCategoryName } from "../../utils/format";
import { getGuestAcquisitionCtaConfig } from "../../config/guestAcquisition";
import {
  DEFAULT_CATEGORIES,
  FALLBACK_CATEGORY,
  getStandardCategoryTranslationKey,
  normalizeStandardCategoryKey,
} from "../../constants";
import {
  createBrowserGuestAcquisitionObservation,
  type GuestAcquisitionAction,
} from "../../guestAcquisition/guestAcquisitionObservation";
import { appCopy, translateAppCopy } from "../../localization/appCopy";

const debug = createDebug("SharedGroceryList");
const CHECKED_ITEM_ACKNOWLEDGEMENT_MS = 2_000;

const isSharedListAccessStateError = (error: string): boolean => {
  const normalizedError = error.toLowerCase();
  return (
    normalizedError.includes("permission denied") ||
    normalizedError.includes("permission-denied") ||
    normalizedError.includes("insufficient permissions") ||
    normalizedError.includes("evaluation error") ||
    normalizedError.includes("false for 'get'") ||
    normalizedError.includes("property expires is undefined")
  );
};

interface EditableSharedListOperation {
  (): Promise<unknown>;
}

export const SharedGroceryListEmptyState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="empty-state" data-testid="shared-grocery-list-empty">
      <p>{translateAppCopy(t, appCopy.sharedGroceryList.empty)}</p>
    </div>
  );
};

export const SharedGroceryListLoadingShell: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div
      className="shared-grocery-list-container loading"
      role="status"
      aria-live="polite"
      data-testid="shared-list-loading-shell"
    >
      <img
        src="/startup-artwork.svg"
        alt=""
        className="startup-artwork"
        data-testid="startup-artwork"
      />
      <span className="visually-hidden">
        {translateAppCopy(t, appCopy.sharedGroceryList.loading)}
      </span>
    </div>
  );
};

const SharedGroceryList: React.FC = () => {
  const { t } = useTranslation();
  const { listId } = useParams<{ listId: string }>();
  const [acquisitionCtaListId, setAcquisitionCtaListId] = useState<
    string | undefined
  >();
  const [acknowledgedItemIds, setAcknowledgedItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const acknowledgedItemIdsRef = useRef<Set<string>>(new Set());
  const acknowledgementTimersRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());

  const {
    metadata,
    loading: metadataLoading,
    error: metadataError,
    isUsingSavedData: metadataUsingSavedData,
  } = useSharedListMetadata(listId);
  const expiresAt =
    metadata?.isPermanent || !metadata?.expires
      ? undefined
      : new Date(metadata.expires).getTime();
  const isExpired = expiresAt !== undefined && expiresAt <= Date.now();
  const canEdit = metadata?.accessConfig.allowEditing === true && !isExpired;
  const {
    items: groceryItems,
    loading: itemsLoading,
    error: itemsError,
    mutationError,
    isSyncing,
    isUsingSavedData: itemsUsingSavedData,
    isOffline,
    queuedOperationsCount,
    addItem: addItemHook,
    toggleItem: toggleItemHook,
    updateItem: updateItemHook,
  } = useSharedListItems(listId, {
    allowEditing: canEdit,
    expiresAt,
    authorityReady: !metadataLoading,
    accessUnavailable:
      Boolean(metadataError) || (!metadataLoading && !metadata),
  });

  const getExpirationDate = () => {
    if (metadata?.expires && !metadata.isPermanent) {
      return metadata.expires;
    }
    return null;
  };
  const expirationDate = useMemo(getExpirationDate, [
    metadata?.expires,
    metadata?.isPermanent,
  ]);
  const timeLeft = useExpirationTimer({ expirationDate });
  const ctaConfig = useMemo(() => getGuestAcquisitionCtaConfig(), []);
  const acquisitionObservation = useMemo(
    () => createBrowserGuestAcquisitionObservation(ctaConfig.mode),
    [ctaConfig.mode],
  );
  const [actionRoute] = useState(() => ({ active: false }));
  const categorizingItems = useMemo(
    () =>
      groceryItems.filter((item) => item.categorizationStatus === "pending"),
    [groceryItems],
  );
  const runEditableSharedListAction = (
    action: GuestAcquisitionAction,
    operation: EditableSharedListOperation,
    blockedMessage: string,
    failureMessage: string,
  ) => {
    if (!canEdit) {
      try {
        acquisitionObservation.observeActionOutcome({
          action,
          result: "blocked",
          reason: "editing_disabled",
        });
      } catch {
        // Observation never changes whether a shopping action is permitted.
      }
      debug.warn(blockedMessage);
      return;
    }

    operation()
      .then(() => {
        if (!actionRoute.active) return;
        setAcquisitionCtaListId(listId);
        try {
          acquisitionObservation.observeActionOutcome({
            action,
            result: "success",
          });
        } catch {
          // Shopping success and CTA eligibility do not depend on analytics.
        }
      })
      .catch((err: unknown) => {
        if (!actionRoute.active) return;
        try {
          acquisitionObservation.observeActionOutcome({
            action,
            result: "failure",
          });
        } catch {
          // The mutation module has already restored the correct item state.
        }
        debug.error(failureMessage, err);
      });
  };

  const restoreAcknowledgedItem = useCallback((id: string) => {
    const timer = acknowledgementTimersRef.current.get(id);
    if (timer !== undefined) clearTimeout(timer);
    acknowledgementTimersRef.current.delete(id);
    setAcknowledgedItemIds((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      acknowledgedItemIdsRef.current = next;
      return next;
    });
  }, []);

  const scheduleItemAcknowledgement = useCallback(
    (id: string, checkedAt: Date) => {
      restoreAcknowledgedItem(id);
      const remainingDelay = Math.max(
        0,
        checkedAt.getTime() + CHECKED_ITEM_ACKNOWLEDGEMENT_MS - Date.now(),
      );
      const timer = setTimeout(() => {
        acknowledgementTimersRef.current.delete(id);
        setAcknowledgedItemIds((current) => {
          const next = new Set(current).add(id);
          acknowledgedItemIdsRef.current = next;
          return next;
        });
      }, remainingDelay);
      acknowledgementTimersRef.current.set(id, timer);
    },
    [restoreAcknowledgedItem],
  );

  useEffect(() => {
    actionRoute.active = true;
    acquisitionObservation.beginRoute(listId);
    setAcquisitionCtaListId(undefined);
    return () => {
      actionRoute.active = false;
      acquisitionObservation.endRoute();
    };
  }, [acquisitionObservation, actionRoute, listId]);

  useEffect(() => {
    const acknowledgementTimers = acknowledgementTimersRef.current;
    acknowledgedItemIdsRef.current = new Set();
    setAcknowledgedItemIds(new Set());
    return () => {
      acknowledgementTimers.forEach((timer) => clearTimeout(timer));
      acknowledgementTimers.clear();
    };
  }, [listId]);

  useEffect(() => {
    const checkedItemIds = new Set(
      groceryItems.filter((item) => item.isChecked).map((item) => item.id),
    );
    setAcknowledgedItemIds((current) => {
      const retained = new Set(
        Array.from(current).filter((id) => checkedItemIds.has(id)),
      );
      acknowledgedItemIdsRef.current = retained;
      return retained.size === current.size ? current : retained;
    });
    groceryItems.forEach((item) => {
      if (!item.isChecked) {
        restoreAcknowledgedItem(item.id);
      } else if (
        !acknowledgedItemIdsRef.current.has(item.id) &&
        !acknowledgementTimersRef.current.has(item.id)
      ) {
        scheduleItemAcknowledgement(item.id, item.checkedAt ?? new Date());
      }
    });
  }, [groceryItems, restoreAcknowledgedItem, scheduleItemAcknowledgement]);

  const visibleGroceryItems = useMemo(
    () => groceryItems.filter((item) => !acknowledgedItemIds.has(item.id)),
    [acknowledgedItemIds, groceryItems],
  );

  // Group items by category (like iOS main grocery list)
  const groupedItems = useMemo(() => {
    const settledItems = visibleGroceryItems.filter(
      (item) => item.categorizationStatus !== "pending",
    );
    // First, collect all unique categories from the items
    const allItemCategories = Array.from(
      new Set(
        settledItems.map(
          (item) =>
            normalizeStandardCategoryKey(item.category) ||
            item.category ||
            FALLBACK_CATEGORY,
        ),
      ),
    );

    const groups: { [category: string]: GroceryItem[] } = {};

    // Initialize all categories we find in the data
    allItemCategories.forEach((category) => {
      groups[category] = [];
    });

    // Always ensure we have the "other" category available
    if (!groups[FALLBACK_CATEGORY]) {
      groups[FALLBACK_CATEGORY] = [];
    }

    // Group items by their actual categories
    settledItems.forEach((item) => {
      const category =
        normalizeStandardCategoryKey(item.category) ||
        item.category ||
        FALLBACK_CATEGORY;

      if (groups[category]) {
        groups[category].push(item);
      } else {
        // Fallback to other if somehow the category doesn't exist
        groups[FALLBACK_CATEGORY].push(item);
      }
    });

    // Create the result array
    const result: { category: string; items: GroceryItem[] }[] = [];

    // Sort categories: standard categories first (in order), then custom categories alphabetically, then "other" last
    const categoriesWithItems = Object.keys(groups).filter(
      (cat) => groups[cat].length > 0,
    );

    // Add standard categories first (in their defined order)
    DEFAULT_CATEGORIES.forEach((category) => {
      if (category === FALLBACK_CATEGORY) {
        return;
      }
      if (groups[category] && groups[category].length > 0) {
        result.push({
          category,
          items: groups[category].sort((a, b) => a.name.localeCompare(b.name)),
        });
      }
    });

    // Add custom/non-standard categories (excluding "other") in alphabetical order
    const customCategories = categoriesWithItems
      .filter(
        (cat) => !DEFAULT_CATEGORIES.includes(cat) && cat !== FALLBACK_CATEGORY,
      )
      .sort((a, b) => a.localeCompare(b));

    customCategories.forEach((category) => {
      result.push({
        category,
        items: groups[category].sort((a, b) => a.name.localeCompare(b.name)),
      });
    });

    // Add "other" category last if it has items
    if (groups[FALLBACK_CATEGORY] && groups[FALLBACK_CATEGORY].length > 0) {
      result.push({
        category: FALLBACK_CATEGORY,
        items: groups[FALLBACK_CATEGORY].sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      });
    }

    return result;
  }, [visibleGroceryItems]);

  const handleAddItem = (itemName: string) => {
    if (!itemName.trim()) {
      debug.warn("Attempted to add item with empty name");
      return;
    }
    runEditableSharedListAction(
      "add_item",
      () => addItemHook(itemName, FALLBACK_CATEGORY),
      "Add item blocked: editing not allowed",
      "Failed to add item via hook",
    );
  };

  const handleToggleItem = (id: string, currentStatus: boolean) => {
    const checkedAt = new Date();
    if (currentStatus) {
      restoreAcknowledgedItem(id);
    } else if (canEdit) {
      scheduleItemAcknowledgement(id, checkedAt);
    }
    runEditableSharedListAction(
      "toggle_item",
      () => toggleItemHook(id, currentStatus, checkedAt),
      "Toggle item blocked: editing not allowed",
      "Failed to toggle item via hook",
    );
  };

  const handleUpdateItem = (id: string, updates: { name?: string }) => {
    runEditableSharedListAction(
      "rename_item",
      () => updateItemHook(id, updates),
      "Update item blocked: editing not allowed",
      "Failed to update item via hook",
    );
  };

  const getCategoryHeader = (
    category: string,
    items: GroceryItem[],
  ): string => {
    const translationKey = getStandardCategoryTranslationKey(category);
    if (translationKey) {
      return t(translationKey) as string;
    }

    const personalCategoryName = items
      .map((item) => item.categoryDisplayName?.trim())
      .find((displayName): displayName is string => !!displayName);
    return personalCategoryName ?? formatCategoryName(category);
  };

  const isLoading = metadataLoading || itemsLoading;
  const overallError =
    metadataError || itemsError || (isExpired ? "permission-denied" : null);
  const isUsingSavedData = metadataUsingSavedData || itemsUsingSavedData;
  const shouldShowAcquisitionCta = acquisitionCtaListId === listId;

  useEffect(() => {
    const connectionState = isOffline ? "offline" : "online";
    if (isLoading) {
      acquisitionObservation.observeSurface({
        kind: "loading",
        connectionState,
      });
      return;
    }
    if (overallError) {
      acquisitionObservation.observeSurface({
        kind: "terminal_error",
        connectionState,
        failure: isSharedListAccessStateError(overallError)
          ? "access_unavailable"
          : "load_error",
      });
      return;
    }
    if (!metadata) {
      acquisitionObservation.observeSurface({
        kind: "not_found",
        connectionState,
      });
      return;
    }

    const expiresAt = metadata.expires
      ? metadata.expires instanceof Date
        ? metadata.expires.getTime()
        : Date.parse(metadata.expires)
      : null;
    acquisitionObservation.observeSurface({
      kind: "available",
      access: {
        editingAllowed: metadata.accessConfig.allowEditing,
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
        permanent: metadata.isPermanent === true,
      },
      connectionState,
      dataSource: isUsingSavedData ? "saved" : "live",
    });
  }, [
    acquisitionObservation,
    isLoading,
    isOffline,
    isUsingSavedData,
    metadata,
    overallError,
    timeLeft,
  ]);

  useEffect(() => {
    acquisitionObservation.observeCtaEligibility(
      !isLoading && !overallError && !!metadata && shouldShowAcquisitionCta,
    );
  }, [
    acquisitionObservation,
    isLoading,
    metadata,
    overallError,
    shouldShowAcquisitionCta,
  ]);

  if (isLoading) {
    return <SharedGroceryListLoadingShell />;
  }

  if (overallError) {
    const isAccessStateError = isSharedListAccessStateError(overallError);
    const errorTitle = isAccessStateError
      ? translateAppCopy(
          t,
          appCopy.sharedGroceryList.error.accessUnavailableTitle,
        )
      : translateAppCopy(t, appCopy.sharedGroceryList.error.title);
    const errorMessage = isAccessStateError
      ? translateAppCopy(
          t,
          appCopy.sharedGroceryList.error.accessUnavailableBody,
        )
      : translateAppCopy(t, appCopy.sharedGroceryList.error.body);

    return (
      <div className="shared-grocery-list-container error">
        <div className="error-content">
          <h2>{errorTitle}</h2>
          <p data-testid="error-message">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (!metadata) {
    return (
      <div className="shared-grocery-list-container error">
        <div className="error-content">
          <p>{translateAppCopy(t, appCopy.sharedGroceryList.notFound)}</p>
        </div>
      </div>
    );
  }

  const listTitle =
    metadata.title?.trim() || (t("sharedGroceryList.title_default") as string);
  const accessStatus = metadata.accessConfig.allowEditing
    ? (t("sharedGroceryList.access_editing") as string)
    : (t("sharedGroceryList.access_read_only") as string);
  const expiryStatus = !metadata.isPermanent
    ? timeLeft
      ? (t("sharedGroceryList.access_expires", { timeLeft }) as string)
      : (t("sharedGroceryList.access_expired") as string)
    : null;

  return (
    <div className="shared-grocery-list-container">
      <header className="list-shell-header">
        <div
          className="brand-lockup"
          aria-label={t("sharedGroceryList.brand_name") as string}
        >
          <img src="/logo192.png" alt="" className="brand-symbol" />
          <span className="brand-wordmark">
            {t("sharedGroceryList.brand_name")}
          </span>
        </div>
        <div className="list-title-row">
          <div>
            <p className="shell-eyebrow">
              {t("sharedGroceryList.shell_eyebrow") as string}
            </p>
            <h1>{listTitle}</h1>
          </div>
          <div className="access-status" aria-label={accessStatus}>
            {accessStatus}
          </div>
        </div>
        <div className="shell-status-row">
          {expiryStatus && <span>{expiryStatus}</span>}
          <span>{t("sharedGroceryList.sync_hint") as string}</span>
        </div>
      </header>

      <section
        className="info-section"
        aria-label={t("sharedGroceryList.status_label") as string}
      >
        <div className="info-row">
          <OfflineStatus
            isOffline={isOffline}
            isUsingSavedData={isUsingSavedData}
            queuedOperationsCount={queuedOperationsCount}
            isSyncing={isSyncing}
          />
        </div>
      </section>

      {mutationError && (
        <p role="alert">
          {translateAppCopy(t, appCopy.sharedGroceryList.mutationFailed)}
        </p>
      )}

      {/* Category Sections */}
      {categorizingItems.length > 0 && (
        <section
          className="items-section categorizing-section"
          aria-live="polite"
        >
          <h2 className="section-header">
            {t("sharedGroceryList.categorizing_section") as string}
          </h2>
          <div className="items-group">
            {categorizingItems.map((item, index) => (
              <GroceryItemRow
                key={item.id}
                item={item}
                isFirst={index === 0}
                isLast={index === categorizingItems.length - 1}
                onToggle={() => handleToggleItem(item.id, item.isChecked)}
                onUpdate={(updates) => handleUpdateItem(item.id, updates)}
                allowEditing={canEdit}
              />
            ))}
          </div>
        </section>
      )}

      {groupedItems.map(({ category, items }) => (
        <section key={category} className="items-section">
          <h2 className="section-header">
            {getCategoryHeader(category, items)}
          </h2>
          <div className="items-group">
            {items.map((item, index) => (
              <GroceryItemRow
                key={item.id}
                item={item}
                isFirst={index === 0}
                isLast={index === items.length - 1}
                onToggle={() => handleToggleItem(item.id, item.isChecked)}
                onUpdate={(updates) => handleUpdateItem(item.id, updates)}
                allowEditing={canEdit}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Empty state when no items at all */}
      {visibleGroceryItems.length === 0 && !isLoading && (
        <SharedGroceryListEmptyState />
      )}

      {shouldShowAcquisitionCta && (
        <aside
          className="app-download-cta"
          data-guest-acquisition-cta
          aria-label={t("sharedGroceryList.cta_label") as string}
        >
          <div>
            <h2>{t(ctaConfig.titleKey) as string}</h2>
            <p>{t(ctaConfig.bodyKey) as string}</p>
          </div>
          <a
            className="app-download-cta-link"
            href={ctaConfig.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => acquisitionObservation.observeCtaClick()}
          >
            {t(ctaConfig.buttonKey) as string}
          </a>
        </aside>
      )}

      {/* Input Bar */}
      {canEdit && <IOSInputBar onAddItem={handleAddItem} disabled={!canEdit} />}
    </div>
  );
};

// Individual grocery item row component matching iOS design
interface GroceryItemRowProps {
  item: GroceryItem;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onUpdate: (updates: { name?: string }) => void;
  allowEditing: boolean;
}

const GroceryItemRow: React.FC<GroceryItemRowProps> = ({
  item,
  isFirst,
  isLast,
  onToggle,
  onUpdate,
  allowEditing,
}) => {
  const { t } = useTranslation();
  const isCategorizing = item.categorizationStatus === "pending";
  const [isEditing, setIsEditing] = React.useState(false);
  const [editName, setEditName] = React.useState(item.name);

  React.useEffect(() => setEditName(item.name), [item.name]);

  const saveRename = () => {
    const name = editName.trim();
    if (name && name !== item.name) onUpdate({ name });
    setIsEditing(false);
  };

  return (
    <div
      className={`grocery-item-row ${isFirst ? "first" : ""} ${isLast ? "last" : ""} ${isCategorizing ? "categorizing" : ""}`}
      data-testid={`grocery-item-row-${item.id}`}
      /* For backward compatibility */
      data-row-testid="grocery-item"
    >
      {/* Checkbox Button */}
      <button
        className="checkbox-button"
        onClick={onToggle}
        aria-label={
          isCategorizing
            ? (t("sharedGroceryList.item_categorizing_aria", {
                itemName: item.name,
              }) as string)
            : (t("sharedGroceryList.toggle_item_aria", {
                itemName: item.name,
              }) as string)
        }
        aria-pressed={item.isChecked}
        data-testid={`checkbox-${item.id}`}
        disabled={isCategorizing}
        type="button"
      >
        {item.isChecked ? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            className="checkbox-icon checked"
          >
            <circle cx="12" cy="12" r="11" fill="var(--color-text-success)" />
            <path
              d="M8 13.5L11 16.5L16 9.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            className="checkbox-icon unchecked"
          >
            <circle
              cx="12"
              cy="12"
              r="11"
              stroke="var(--color-text-secondary)"
              strokeWidth="2"
              fill="none"
            />
          </svg>
        )}
      </button>

      {/* Item Content */}
      <div className="item-content">
        {isEditing ? (
          <input
            type="text"
            name="itemName"
            autoComplete="off"
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                saveRename();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                setEditName(item.name);
                setIsEditing(false);
              }
            }}
            className="edit-name-input"
            placeholder={
              t("sharedGroceryList.edit_item_name_placeholder") as string
            }
            autoFocus
          />
        ) : (
          <div className="item-text">
            <span
              className={`item-name ${item.isChecked ? "checked" : ""}`}
              role={isCategorizing ? undefined : "button"}
              tabIndex={isCategorizing ? undefined : 0}
              onClick={
                isCategorizing
                  ? undefined
                  : allowEditing
                    ? () => setIsEditing(true)
                    : onToggle
              }
              onKeyDown={
                isCategorizing
                  ? undefined
                  : (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (allowEditing) setIsEditing(true);
                        else onToggle();
                      }
                    }
              }
            >
              {item.name}
            </span>
            {isCategorizing && (
              <span className="item-status">
                {t("sharedGroceryList.item_categorizing") as string}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Replacing the capability replaces both metadata and mutation lifetimes before
// a new list can inherit authority or presentation from the previous route.
const SharedGroceryListRoute: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  return <SharedGroceryList key={listId} />;
};

export default SharedGroceryListRoute;
